import React, { useState, useEffect } from 'react';
import { matchPath } from 'react-router-dom';
import { Link, Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import AppRoutes from './AppRoutes';
import * as Sentry from '@sentry/react';
import { initSplitTestOverride } from './utils/feature-flags';
import StandaloneScenarioSeedPanel from './components/shared/StandaloneScenarioSeedPanel';
import DevLimitScenarioPanel from './components/shared/DevLimitScenarioPanel';
import UnseededStandalonePlaceholder from './components/shared/UnseededStandalonePlaceholder';
import { isUnseededStandalone } from './utils/env';
import { initializeDevScenarios } from './dev-scenarios/browser-state';
import { addBillingDiagnostics } from './utils/billing-diagnostics';
import './fonts/inter.css';

// Initialize split test override at module load time (runs once)
// This must run before any React components render to ensure the cookie is set
// before Netlify's CDN makes branch decisions
initSplitTestOverride();

const history = createBrowserHistory();

let _isMounted = false;

const sentryDNS = process.env.REACT_APP_SENTRY || null;
const environment = process.env.REACT_APP_ENVIRONMENT || 'production';
const HEADER_SUBTITLE_MIN_HEIGHT_PX = 72;

// Setup routes for Sentry, so we can use parameterized routes
// See https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/#parameterized-transaction-names
const routes = [
    { path: '/', name: '/' }, // Dashboard
    { path: '/billing', name: '/' }, // Dashboard
    { path: '/billing/plans', name: '/plans' }, // Plans
    { path: '/plans', name: '/plans' }, // Plans
    { path: '/checkout/:id', name: '/checkout/:id' }, // Checkout
    { path: '/billing/checkout/:id', name: '/checkout/:id' }, // Checkout
    { path: '/checkout', name: '/checkout' }, // Checkout
    { path: '/billing/checkout', name: '/checkout' }, // Checkout
    { path: '/support', name: '/support' }, // Support
    { path: '/backups', name: '/backups' }, // Backups
    { path: '/domain', name: '/domain' }, // Domain
    { path: '/domain/custom', name: '/domain/custom' }, // Custom Domain
    { path: '/domain/sending', name: '/domain/sending' }, // Sending Domain
];

Sentry.init({
    dsn: sentryDNS,
    beforeSend: addBillingDiagnostics,
    integrations: [
        Sentry.reactRouterV5BrowserTracingIntegration({ history, routes, matchPath }),
        Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
            block: ['iframe'],
        }),
    ],
    release: process.env.REACT_APP_COMMIT_REF,
    environment,
    tracesSampleRate: environment === 'production' ? 0.2 : 1.0,
    replaysSessionSampleRate: 0,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 0.5,
});

/**
 * Root component for the Billing Management App. Wires up Sentry, the router,
 * and the standalone seeding toolbar, and short-circuits `AppRoutes` with an
 * `UnseededStandalonePlaceholder` when running in standalone mode before a
 * scenario user has been seeded (so Dashboard does not fire Daisy calls with
 * a non-existent `test-email`).
 *
 * @returns {JSX.Element}
 */
function App() {
    const [title, setTitle] = useState('Billing and domain settings');
    const [headerLogo, setHeaderLogo] = useState(
        `${process.env.PUBLIC_URL}/logos/ghost-logo-black-01.png`,
    );
    const unseededStandalone = isUnseededStandalone();

    useEffect(() => {
        // This clears the preboot watchdog once React mounts; Admin readiness is reported separately by AppRoutes.
        window.__bmaBoot?.markReady?.();

        if (!_isMounted) {
            _isMounted = true;
            initializeDevScenarios();
            const random = Math.floor(Math.random() * 10) + 1;
            const randomString = random > 9 ? random : `0${random}`;
            // The prefix is required because we're loading the images from the /public folder
            const logoString = `${process.env.PUBLIC_URL}/logos/ghost-logo-black-${randomString}.png`;

            setHeaderLogo(logoString);
        }
    }, []);

    /**
     * Updates the header subtitle shown under the Ghost(Pro) logo. Invoked by
     * child routes via the `updateTitle` prop so each page can announce itself.
     *
     * @param {string} newTitle
     * @returns {void}
     */
    const handleTitleUpdate = (newTitle) => {
        setTitle(newTitle);
    };

    return (
        <Sentry.ErrorBoundary>
            <div className="viewport" data-test-id="app-container">
                <div className="container">
                    <Router history={history}>
                        <header className="mainhead">
                            <Link className="home-link" to="/">
                                <img id="ghost-logo" src={headerLogo} alt="Ghost" />
                                <svg
                                    className="pro-logo-svg"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 214 114"
                                >
                                    <rect
                                        width="214"
                                        height="114"
                                        rx="23"
                                        fill="url(#paint0_linear)"
                                    />
                                    <rect
                                        x="1.5"
                                        y="1.5"
                                        width="211"
                                        height="111"
                                        rx="21.5"
                                        stroke="#000"
                                        strokeOpacity=".06"
                                        strokeWidth="3"
                                    />
                                    <g filter="url(#filter0_d)">
                                        <path
                                            d="M42.866 31.639c2.156-.41 4.517-.719 7.084-.924a79.66 79.66 0 017.777-.385c2.67 0 5.339.231 8.008.693 2.72.41 5.16 1.283 7.315 2.618 2.156 1.283 3.901 3.106 5.236 5.467 1.386 2.31 2.08 5.339 2.08 9.086 0 3.285-.591 6.109-1.772 8.47-1.18 2.31-2.746 4.235-4.697 5.775-1.95 1.489-4.184 2.592-6.699 3.311a29.328 29.328 0 01-7.7 1.001h-3.002l-1.849-.154a13.004 13.004 0 01-1.232-.154V85H42.866V31.639zm15.708 7.777c-1.026 0-2.002.051-2.926.154-.924.051-1.668.128-2.233.231V57.28c.206.051.514.103.924.154.41.051.847.103 1.31.154h2.309c1.386 0 2.747-.128 4.081-.385 1.386-.257 2.618-.719 3.696-1.386 1.078-.719 1.925-1.694 2.541-2.926.668-1.232 1.001-2.849 1.001-4.851 0-1.694-.308-3.08-.924-4.158-.616-1.13-1.437-2.028-2.464-2.695-.975-.667-2.104-1.13-3.388-1.386a19.972 19.972 0 00-3.927-.385zm29.615-7.777a66.7 66.7 0 013.773-.539 58.478 58.478 0 014.081-.462c1.386-.103 2.72-.18 4.004-.231a89.176 89.176 0 013.542-.077c2.515 0 4.979.257 7.392.77 2.464.462 4.646 1.283 6.545 2.464a12.88 12.88 0 014.62 4.774c1.181 2.002 1.771 4.492 1.771 7.469 0 2.207-.231 4.132-.693 5.775-.411 1.591-1.001 3.003-1.771 4.235a13.18 13.18 0 01-2.849 3.234 25.14 25.14 0 01-3.542 2.387L128.075 85h-12.166l-10.857-21.098h-6.314V85H88.189V31.639zm16.17 8.085c-1.078 0-2.156.026-3.234.077-1.027.051-1.822.154-2.387.308V55.74h4.158c2.926 0 5.313-.667 7.161-2.002 1.848-1.335 2.772-3.542 2.772-6.622 0-2.31-.719-4.107-2.156-5.39-1.437-1.335-3.542-2.002-6.314-2.002zm27.998 18.326c0-4.363.411-8.29 1.232-11.781.821-3.49 2.079-6.417 3.773-8.778 1.694-2.413 3.799-4.26 6.314-5.544 2.567-1.283 5.57-1.925 9.009-1.925 3.696 0 6.827.693 9.394 2.079a16.835 16.835 0 016.237 5.775c1.643 2.464 2.823 5.416 3.542 8.855.77 3.44 1.155 7.212 1.155 11.319 0 8.778-1.694 15.657-5.082 20.636-3.337 4.928-8.419 7.392-15.246 7.392-3.696 0-6.853-.693-9.471-2.079-2.567-1.386-4.671-3.311-6.314-5.775-1.591-2.464-2.746-5.416-3.465-8.855-.719-3.44-1.078-7.212-1.078-11.319zm11.088 0c0 2.618.103 5.056.308 7.315.257 2.259.693 4.235 1.309 5.929.667 1.643 1.591 2.952 2.772 3.927 1.232.975 2.849 1.463 4.851 1.463 1.797 0 3.285-.36 4.466-1.078 1.181-.77 2.13-1.925 2.849-3.465.719-1.54 1.206-3.465 1.463-5.775.308-2.361.462-5.133.462-8.316a79.33 79.33 0 00-.308-7.161c-.154-2.259-.565-4.235-1.232-5.929-.616-1.694-1.54-3.029-2.772-4.004-1.232-1.027-2.875-1.54-4.928-1.54-3.593 0-6.032 1.566-7.315 4.697-1.283 3.131-1.925 7.777-1.925 13.937z"
                                            fill="#fff"
                                        />
                                    </g>
                                    <defs>
                                        <linearGradient
                                            id="paint0_linear"
                                            x1="151"
                                            y1="114"
                                            x2="66.316"
                                            y2="-11.129"
                                            gradientUnits="userSpaceOnUse"
                                        >
                                            <stop stopColor="#009B7F" />
                                            <stop offset="1" stopColor="#60D20D" />
                                        </linearGradient>
                                        <filter
                                            id="filter0_d"
                                            x="38.866"
                                            y="28.022"
                                            width="138.147"
                                            height="64.056"
                                            filterUnits="userSpaceOnUse"
                                            colorInterpolationFilters="sRGB"
                                        >
                                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                            <feColorMatrix
                                                in="SourceAlpha"
                                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                            />
                                            <feOffset dy="2" />
                                            <feGaussianBlur stdDeviation="2" />
                                            <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
                                            <feBlend
                                                in2="BackgroundImageFix"
                                                result="effect1_dropShadow"
                                            />
                                            <feBlend
                                                in="SourceGraphic"
                                                in2="effect1_dropShadow"
                                                result="shape"
                                            />
                                        </filter>
                                    </defs>
                                </svg>
                            </Link>
                            <p
                                style={{ minHeight: `${HEADER_SUBTITLE_MIN_HEIGHT_PX}px` }}
                                dangerouslySetInnerHTML={{ __html: title }}
                            ></p>
                        </header>
                        <StandaloneScenarioSeedPanel />
                        <DevLimitScenarioPanel />
                        {unseededStandalone ? (
                            <UnseededStandalonePlaceholder />
                        ) : (
                            <AppRoutes updateTitle={handleTitleUpdate}></AppRoutes>
                        )}
                    </Router>
                </div>
            </div>
        </Sentry.ErrorBoundary>
    );
}

export default Sentry.withProfiler(App);
